import {
  useEffect,
  useState,
} from "react";

import {
  useAuth,
} from "../../auth/AuthContext";

import {
  loadDashboardData,
} from "./dashboard.service";

import "./Dashboard.css";


type DashboardPage =
  | "dashboard"
  | "comandas"
  | "estoque"
  | "quadras"
  | "turmas"
  | "clientes"
  | "financeiro";


type DashboardProps = {
  onNavigate: (
    page: DashboardPage,
  ) => void;
};


type Booking = {
  id?: string;
  cliente?: string;
  court?: string;
  hour?: number;
  duration?: number;
  pago?: boolean;
};


type DashboardData = {
  saldo: number;

  entradasHoje: number;

  saidasHoje: number;

  comandasAbertas: number;

  valorComandasAbertas: number;

  reservasHoje: number;

  reservasPagasHoje: number;

  reservasPendentesHoje: number;

  estoqueBaixo: number;

  proximasReservas:
    Booking[];
};


function getGreeting() {
  const hour =
    new Date().getHours();

  if (hour < 12) {
    return "Bom dia";
  }

  if (hour < 18) {
    return "Boa tarde";
  }

  return "Boa noite";
}


function formatBRL(
  value: number,
) {
  return new Intl.NumberFormat(
    "pt-BR",
    {
      style: "currency",
      currency: "BRL",
    },
  ).format(
    value,
  );
}


function formatHour(
  hour = 0,
) {
  const h =
    Math.floor(
      hour,
    );

  const minutes =
    Math.abs(
      hour % 1,
    ) >= 0.49
      ? "30"
      : "00";

  return `${String(
    h,
  ).padStart(
    2,
    "0",
  )}:${minutes}`;
}


export default function Dashboard({
  onNavigate,
}: DashboardProps) {
  const {
    profile,
  } =
    useAuth();


  const firstName =
    profile?.nome
      ?.trim()
      .split(
        " ",
      )[0] ||
    "Usuário";


  const [
    data,
    setData,
  ] =
    useState<
      DashboardData | null
    >(
      null,
    );


  const [
    loading,
    setLoading,
  ] =
    useState(
      true,
    );


  const [
    error,
    setError,
  ] =
    useState(
      "",
    );


  useEffect(
    () => {
      async function load() {
        try {
          setLoading(
            true,
          );

          const result =
            await loadDashboardData();

          setData(
            result,
          );

          setError(
            "",
          );
        } catch (err) {
          console.error(
            err,
          );

          setError(
            "Não foi possível carregar os dados do Dashboard.",
          );
        } finally {
          setLoading(
            false,
          );
        }
      }

      load();
    },
    [],
  );


  if (loading) {
    return (
      <section className="dashboard-page">
        <div className="dashboard-loading">
          Carregando dados da Arena...
        </div>
      </section>
    );
  }


  if (
    error ||
    !data
  ) {
    return (
      <section className="dashboard-page">
        <div className="dashboard-error">
          <strong>
            Erro ao carregar Dashboard
          </strong>

          <p>
            {error}
          </p>
        </div>
      </section>
    );
  }


  const cards = [
    {
      label:
        "Saldo do dia",

      value:
        formatBRL(
          data.saldo,
        ),

      tone:
        data.saldo >= 0
          ? "success"
          : "danger",

      icon:
        "💰",

      detail:
        `Entradas ${formatBRL(
          data.entradasHoje,
        )} · Saídas ${formatBRL(
          data.saidasHoje,
        )}`,

      page:
        "financeiro" as DashboardPage,
    },

    {
      label:
        "Comandas abertas",

      value:
        String(
          data.comandasAbertas,
        ),

      tone:
        data.comandasAbertas >
        0
          ? "warning"
          : "success",

      icon:
        "☕",

      detail:
        `${formatBRL(
          data.valorComandasAbertas,
        )} em aberto`,

      page:
        "comandas" as DashboardPage,
    },

    {
      label:
        "Reservas hoje",

      value:
        String(
          data.reservasHoje,
        ),

      tone:
        "info",

      icon:
        "▦",

      detail:
        `${data.reservasPagasHoje} pagas · ${data.reservasPendentesHoje} pendentes`,

      page:
        "quadras" as DashboardPage,
    },

    {
      label:
        "Estoque baixo",

      value:
        String(
          data.estoqueBaixo,
        ),

      tone:
        data.estoqueBaixo >
        0
          ? "danger"
          : "success",

      icon:
        "📦",

      detail:
        data.estoqueBaixo >
        0
          ? "Itens abaixo do mínimo"
          : "Estoque dentro do esperado",

      page:
        "estoque" as DashboardPage,
    },
  ];


  return (
    <section className="dashboard-page">
      <div className="dashboard-header">
        <div>
          <span className="dashboard-eyebrow">
            SUNSET ONE
          </span>

          <h2>
            {getGreeting()}
            {", "}
            <span className="dashboard-user-name">
              {firstName}
            </span>
          </h2>

          <p>
            Resumo da operação de hoje
          </p>
        </div>
      </div>


      <div className="dashboard-grid">
        {cards.map(
          (
            card,
          ) => (
            <button
              type="button"
              className="dashboard-card dashboard-card-button"
              key={
                card.label
              }
              onClick={() =>
                onNavigate(
                  card.page,
                )
              }
            >
              <div className="dashboard-card-top">
                <span>
                  {card.label}
                </span>

                <b>
                  {card.icon}
                </b>
              </div>

              <strong
                className={
                  card.tone
                }
              >
                {
                  card.value
                }
              </strong>

              <small className="dashboard-card-detail">
                {
                  card.detail
                }
              </small>
            </button>
          ),
        )}
      </div>


      <section className="dashboard-section">
        <div className="dashboard-section-header">
          <div>
            <span className="dashboard-eyebrow">
              AGENDA
            </span>

            <h3>
              Próximas reservas de hoje
            </h3>
          </div>
        </div>


        {data.proximasReservas.length ===
        0 ? (
          <div className="dashboard-empty">
            Nenhuma reserva para hoje.
          </div>
        ) : (
          <div className="booking-list">
            {data.proximasReservas.map(
              (
                booking,
                index,
              ) => (
                <button
                  type="button"
                  className="booking-row booking-row-button"
                  key={
                    booking.id ||
                    index
                  }
                  onClick={() =>
                    onNavigate(
                      "quadras",
                    )
                  }
                >
                  <div>
                    <strong>
                      {formatHour(
                        booking.hour,
                      )}
                    </strong>

                    <span>
                      {booking.cliente ||
                        "Cliente"}
                    </span>

                    <small>
                      {booking.court ||
                        "Quadra"}

                      {" · "}

                      {booking.duration ||
                        0}
                      h
                    </small>
                  </div>

                  <b
                    className={
                      booking.pago
                        ? "booking-paid"
                        : "booking-pending"
                    }
                  >
                    {booking.pago
                      ? "Pago"
                      : "Pendente"}
                  </b>
                </button>
              ),
            )}
          </div>
        )}
      </section>


      <section className="dashboard-section dashboard-quick-section">
        <span className="dashboard-eyebrow">
          OPERAÇÃO
        </span>

        <h3>
          Acesso rápido
        </h3>


        <div className="quick-actions">
          <button
            type="button"
            onClick={() =>
              onNavigate(
                "comandas",
              )
            }
          >
            ☕ Comandas
          </button>

          <button
            type="button"
            onClick={() =>
              onNavigate(
                "quadras",
              )
            }
          >
            ▦ Quadras
          </button>

          <button
            type="button"
            onClick={() =>
              onNavigate(
                "estoque",
              )
            }
          >
            📦 Estoque
          </button>
        </div>
      </section>
    </section>
  );
}